import React from 'react';
import type { WhatsAppTemplate, WhatsAppTemplateButton } from '../../models/MasterAdmin/whatsappCampaign';
import type { WhatsAppContextField } from '../../models/MasterAdmin/whatsappHub';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';
import NotificationDeepLinkPicker from './NotificationDeepLinkPicker';
import { previewDeepLinkFromCampaign } from './whatsappDeepLinkPreview';
import { encodeDeepLinkToken } from './whatsappSendRecipientFields';
import { mergeNotificationFields } from './whatsappNotificationFields';

export type SendButtonLinkSlot = {
  buttonIndex: number;
  mode: 'template' | 'deep_link' | 'static';
  staticUrl?: string;
  deepLinkCampaignId?: string | null;
  deepLinkUrlType?: 'short' | 'full' | 'dynamic';
  deepLinkParamBindings?: Record<string, string>;
};

const inputClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800';

export function listUrlButtons(template?: WhatsAppTemplate | null) {
  return (template?.buttons || [])
    .map((btn, index) => ({ btn, index }))
    .filter(({ btn }) => {
      const t = String(btn.type || '').toUpperCase();
      return t === 'URL' || t === 'CALL_TO_ACTION' || t === 'WEBSITE';
    });
}

function isDynamicTemplateButtonUrl(url?: string | null) {
  return /\{\{\s*1\s*\}\}/.test(String(url || ''));
}

export function defaultSendButtonSlots(template?: WhatsAppTemplate | null): SendButtonLinkSlot[] {
  return listUrlButtons(template).map(({ index, btn }) => ({
    buttonIndex: index,
    mode: 'template',
    deepLinkCampaignId: btn.deepLinkCampaignId || null,
    deepLinkUrlType: btn.deepLinkUrlType || 'short',
    deepLinkParamBindings: btn.deepLinkParamBindings || {},
    staticUrl: btn.url || '',
  }));
}

export function sendButtonSlotsToButtonValues(
  slots: SendButtonLinkSlot[],
  template?: WhatsAppTemplate | null
): Record<string, string[]> | null {
  const buttons = template?.buttons || [];
  const out: Record<string, string[]> = {};
  for (const slot of slots) {
    if (slot.mode === 'template') continue;
    const btn = buttons[slot.buttonIndex];
    if (btn && !isDynamicTemplateButtonUrl(btn.url)) continue;
    if (slot.mode === 'deep_link' && slot.deepLinkCampaignId) {
      out[String(slot.buttonIndex)] = [
        encodeDeepLinkToken({
          c: slot.deepLinkCampaignId,
          t: slot.deepLinkUrlType || 'short',
          p: slot.deepLinkParamBindings || {},
        }),
      ];
      continue;
    }
    const url = String(slot.staticUrl || '').trim();
    if (slot.mode === 'static' && url) {
      out[String(slot.buttonIndex)] = [url];
    }
  }
  return Object.keys(out).length ? out : null;
}

export function applySendButtonPreview(
  buttons: WhatsAppTemplateButton[] | undefined,
  slots: SendButtonLinkSlot[],
  campaignsById: Map<string, DeepLinkCampaign>
): WhatsAppTemplateButton[] {
  return (buttons || []).map((btn, index) => {
    const slot = slots.find((s) => s.buttonIndex === index);
    if (!slot || slot.mode === 'template') return btn;
    if (slot.mode === 'deep_link' && slot.deepLinkCampaignId) {
      const url =
        previewDeepLinkFromCampaign(
          {
            deepLinkCampaignId: slot.deepLinkCampaignId,
            deepLinkUrlType: slot.deepLinkUrlType,
            deepLinkParamBindings: slot.deepLinkParamBindings,
          },
          campaignsById
        ) || btn.url;
      return { ...btn, url };
    }
    if (slot.mode === 'static' && slot.staticUrl?.trim()) {
      return { ...btn, url: slot.staticUrl.trim() };
    }
    return btn;
  });
}

type Props = {
  template?: WhatsAppTemplate | null;
  slots: SendButtonLinkSlot[];
  onChange: (next: SendButtonLinkSlot[]) => void;
  campaigns: DeepLinkCampaign[];
  contextFields: WhatsAppContextField[];
  onCampaignCreated: (campaign: DeepLinkCampaign) => void;
  disabled?: boolean;
};

export default function WhatsAppSendButtonLinkEditor({
  template,
  slots,
  onChange,
  campaigns,
  contextFields,
  onCampaignCreated,
  disabled,
}: Props) {
  const urlButtons = listUrlButtons(template);
  const allFields = React.useMemo(() => mergeNotificationFields(contextFields), [contextFields]);

  if (!urlButtons.length) return null;

  const updateSlot = (buttonIndex: number, patch: Partial<SendButtonLinkSlot>) => {
    onChange(slots.map((s) => (s.buttonIndex === buttonIndex ? { ...s, ...patch } : s)));
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div>
        <p className="text-sm font-medium text-gray-900 dark:text-white">Button links</p>
        <p className="mt-0.5 text-xs text-gray-500">
          Only website buttons created with a dynamic path ({'{{1}}'}) can be changed when sending.
          Fixed URLs stay as approved on the template.
        </p>
      </div>
      {urlButtons.map(({ btn, index }) => {
        const slot =
          slots.find((s) => s.buttonIndex === index) || defaultSendButtonSlots(template).find((s) => s.buttonIndex === index);
        if (!slot) return null;
        const canOverride = isDynamicTemplateButtonUrl(btn.url);
        return (
          <div key={index} className="rounded-lg border border-gray-100 p-3 dark:border-gray-800">
            <p className="mb-2 text-xs font-medium text-gray-700 dark:text-gray-200">
              {btn.text || `Button ${index + 1}`}
              {btn.url ? (
                <span className="ml-1 font-normal text-gray-400">· template {btn.url}</span>
              ) : null}
            </p>
            {!canOverride ? (
              <p className="text-[11px] text-amber-700 dark:text-amber-300">
                This button is a fixed URL on the approved template, so WhatsApp will not accept a
                different link at send time.
              </p>
            ) : null}
            {canOverride ? (
            <>
            <select
              value={slot.mode}
              disabled={disabled}
              onChange={(e) => {
                const mode = e.target.value as SendButtonLinkSlot['mode'];
                if (mode === 'deep_link') {
                  updateSlot(index, {
                    mode,
                    deepLinkCampaignId: slot.deepLinkCampaignId || campaigns[0]?.id || null,
                    deepLinkUrlType: slot.deepLinkUrlType || 'short',
                    deepLinkParamBindings: slot.deepLinkParamBindings || {},
                  });
                  return;
                }
                if (mode === 'static') {
                  updateSlot(index, { mode, staticUrl: slot.staticUrl || btn.url || 'https://' });
                  return;
                }
                updateSlot(index, { mode });
              }}
              className={inputClass}
            >
              <option value="template">Keep template link</option>
              <option value="deep_link">Deep link campaign</option>
              <option value="static">Custom URL</option>
            </select>
            {slot.mode === 'deep_link' ? (
              <div className="mt-2">
                <NotificationDeepLinkPicker
                  compact
                  value={{
                    deepLinkCampaignId: slot.deepLinkCampaignId,
                    deepLinkUrlType: slot.deepLinkUrlType,
                    deepLinkParamBindings: slot.deepLinkParamBindings,
                  }}
                  onChange={(next) =>
                    updateSlot(index, {
                      deepLinkCampaignId: next.deepLinkCampaignId,
                      deepLinkUrlType: next.deepLinkUrlType,
                      deepLinkParamBindings: next.deepLinkParamBindings,
                    })
                  }
                  contextFields={allFields}
                  campaigns={campaigns}
                  onCampaignCreated={onCampaignCreated}
                  suggestedCampaignName={`WhatsApp send · ${btn.text || `button ${index + 1}`}`}
                />
              </div>
            ) : null}
            {slot.mode === 'static' ? (
              <input
                value={slot.staticUrl || ''}
                disabled={disabled}
                onChange={(e) => updateSlot(index, { staticUrl: e.target.value })}
                placeholder="https://vedika.health/…"
                className={`${inputClass} mt-2`}
              />
            ) : null}
            </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
